import React, { useEffect, useRef, useState, useCallback } from "react";
import Topbar from "../components/Topbar";
import DynamicRegistrationForm from "./DynamicRegistrationForm";
import ThankYouMessage from "../components/ThankYouMessage";
import useIsMobile from "../hooks/useIsMobile";
import Footer from "../components/Footer";
/*
  Speakers.jsx - with mobile view support and corrected redirect
  - Mobile-responsive layout (desktop + mobile views)
  - Backend sends confirmation email automatically (ACK only, no ticket)
  - Simplified thank-you flow with smooth redirect
*/

function getApiBaseFromEnvOrWindow() {
  if (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE)
    return String(process.env.REACT_APP_API_BASE).replace(/\/$/, "");

  if (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE_URL)
    return String(process.env.REACT_APP_API_BASE_URL).replace(/\/$/, "");

  if (typeof window !== "undefined" && window.__API_BASE__)
    return String(window.__API_BASE__).replace(/\/$/, "");

  if (typeof window !== "undefined" && window.location?.origin)
    return window.location.origin.replace(/\/$/, "");

  return "";
}

function apiUrl(path) {
  const base = getApiBaseFromEnvOrWindow();
  if (!path) return base;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${base.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

function normalizeAdminUrl(url) {
  if (!url) return "";
  const t = String(url).trim();
  if (!t) return "";

  // absolute URLs → trust them
  if (/^https?:\/\//i.test(t)) return t;

  // relative → API base (NOT frontend base)
  const base = getApiBaseFromEnvOrWindow();
  if (t.startsWith("/")) return `${base}${t}`;
  return `${base}/${t}`;
}

function isEmailLike(v) {
  return typeof v === "string" && /\S+@\S+\.\S+/.test(v);
}

/* UI helpers */
function EventDetailsBlock({ event }) {
  if (!event)
    return <div className="text-[#21809b]">No event details available</div>;
  const logoGradient =
    "linear-gradient(90deg, #ffba08 0%, #19a6e7 60%, #21809b 100%)";
  const logoBlue = "#21809b";
  const logoDark = "#196e87";
  return (
    <div className="flex flex-col items-center justify-center h-full w-full mt-6">
      <div
        className="font-extrabold text-3xl sm:text-5xl mb-3 text-center"
        style={{
          background: logoGradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {event?.name || "Event Name"}
      </div>
      <div
        className="text-xl sm:text-2xl font-bold mb-1 text-center"
        style={{ color: logoBlue }}
      >
        {event?.date || "Event Date"}
      </div>
      <div
        className="text-base sm:text-xl font-semibold text-center"
        style={{ color: logoDark }}
      >
        {event?.venue || "Event Venue"}
      </div>
      {event?.tagline && (
        <div className="text-base sm:text-xl font-semibold text-center text-[#21809b] mt-2">
          {event.tagline}
        </div>
      )}
    </div>
  );
}

/* ---------- Component ---------- */
export default function Speakers() {
  const [config, setConfig] = useState(null);
  const [canonicalEvent, setCanonicalEvent] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [speakerId, setSpeakerId] = useState(null);
  const [speaker, setSpeaker] = useState(null);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  const videoRef = useRef(null);
  const isMobile = useIsMobile(1024);

  const videoUrl =
    config?.backgroundMedia?.type === "video"
      ? config.backgroundMedia.url
      : null;
  const [primaryColor, setPrimaryColor] = useState("#196e87");

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("admin:topbar") || "{}");
      if (saved.primaryColor) setPrimaryColor(saved.primaryColor);
    } catch {}
    function onUpdate(e) {
      if (e?.detail?.primaryColor) setPrimaryColor(e.detail.primaryColor);
    }
    window.addEventListener("admin:topbar-updated", onUpdate);
    return () => window.removeEventListener("admin:topbar-updated", onUpdate);
  }, []);
  // Redirect after successful submission
  useEffect(() => {
    if (submissionComplete) {
      const timer = setTimeout(() => {
        window.location.replace("https://www.railtransexpo.com/");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [submissionComplete]);

  const fetchCanonicalEvent = useCallback(async () => {
    try {
      const url = apiUrl("/api/configs/event-details");
      const res = await fetch(`${url}?cb=${Date.now()}`, {
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
      });
      if (res.ok) {
        const js = await res.json().catch(() => ({}));
        const val = js && js.value !== undefined ? js.value : js;
        if (val && typeof val === "object" && Object.keys(val).length) {
          setCanonicalEvent({
            name: val.name || "",
            date: val.date || "",
            venue: val.venue || "",
            time: val.time || "",
            tagline: val.tagline || "",
          });
          return;
        }
      }
      const legacy = await fetch(
        `${apiUrl("/api/event-details")}?cb=${Date.now()}`,
        {
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "ngrok-skip-browser-warning": "69420",
          },
        },
      ).catch(() => null);
      if (legacy && legacy.ok) {
        const ljs = await legacy.json().catch(() => ({}));
        setCanonicalEvent({
          name: ljs.name || "",
          date: ljs.date || "",
          venue: ljs.venue || "",
          time: ljs.time || "",
          tagline: ljs.tagline || "",
        });
        return;
      }
      setCanonicalEvent(null);
    } catch (e) {
      console.warn("fetchCanonicalEvent failed", e);
      setCanonicalEvent(null);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(apiUrl("/api/speaker-config"), {
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
      });
      const cfg = res.ok ? await res.json().catch(() => ({})) : {};
      const normalized = { ...(cfg || {}) };

      if (normalized.backgroundMedia && normalized.backgroundMedia.url) {
        normalized.backgroundMedia = {
          type: normalized.backgroundMedia.type || "image",
          url: normalizeAdminUrl(normalized.backgroundMedia.url),
        };
      } else {
        const candidate =
          normalized.backgroundVideo ||
          normalized.backgroundImage ||
          normalized.background_image ||
          "";
        if (candidate) {
          const isVideo =
            typeof candidate === "string" &&
            /\.(mp4|webm|ogg)(\?|$)/i.test(candidate);
          normalized.backgroundMedia = {
            type: isVideo ? "video" : "image",
            url: normalizeAdminUrl(candidate),
          };
        } else {
          normalized.backgroundMedia = { type: "image", url: "" };
        }
      }

      normalized.termsUrl = normalized.termsUrl
        ? normalizeAdminUrl(normalized.termsUrl)
        : normalized.terms || "";
      normalized.termsText = normalized.termsText || "";
      normalized.termsLabel = normalized.termsLabel || "Terms & Conditions";
      normalized.termsRequired = !!normalized.termsRequired;
      normalized.fields = Array.isArray(normalized.fields)
        ? normalized.fields
        : [];

      // remove auto-accept fields
      normalized.fields = normalized.fields.filter((f) => {
        if (!f || typeof f !== "object") return false;
        const name = (f.name || "")
          .toString()
          .toLowerCase()
          .replace(/\s+/g, "");
        const label = (f.label || "").toString().toLowerCase();
        if (["accept_terms", "acceptterms", "i_agree", "agree"].includes(name))
          return false;
        if (
          f.type === "checkbox" &&
          (label.includes("i agree") ||
            label.includes("accept the terms") ||
            label.includes("terms & conditions") ||
            label.includes("terms and conditions"))
        )
          return false;
        return true;
      });

      normalized.fields = normalized.fields.map((f) => {
        if (!f || !f.name) return f;
        const nameLabel = (f.name + " " + (f.label || "")).toLowerCase();
        const isEmailField = f.type === "email" || /email/.test(nameLabel);
        if (isEmailField) {
          const fm = Object.assign({}, f.meta || {});
          if (fm.useOtp === undefined) fm.useOtp = true;
          return { ...f, meta: fm };
        }
        return f;
      });

      normalized.images = Array.isArray(normalized.images)
        ? normalized.images.map(normalizeAdminUrl)
        : [];
      normalized.eventDetails =
        typeof normalized.eventDetails === "object" && normalized.eventDetails
          ? normalized.eventDetails
          : {};

      setConfig(normalized);
    } catch (e) {
      console.error("fetchConfig error", e);
      setConfig({
        fields: [],
        images: [],
        backgroundMedia: { type: "image", url: "" },
        eventDetails: {},
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchCanonicalEvent();

    const onCfg = () => {
      fetchConfig();
      fetchCanonicalEvent();
    };
    const onConfigUpdated = (e) => {
      const key = e && e.detail && e.detail.key ? e.detail.key : null;
      if (!key || key === "event-details")
        fetchCanonicalEvent().catch(() => {});
    };

    window.addEventListener("speaker-config-updated", onCfg);
    window.addEventListener("config-updated", onConfigUpdated);
    window.addEventListener("event-details-updated", fetchCanonicalEvent);

    return () => {
      window.removeEventListener("speaker-config-updated", onCfg);
      window.removeEventListener("visitor-config-updated", onCfg);
      window.removeEventListener("config-updated", onConfigUpdated);
      window.removeEventListener("event-details-updated", fetchCanonicalEvent);
    };
  }, [fetchConfig, fetchCanonicalEvent]);

  /* Handle form submit:  validate and save immediately.  Backend sends confirmation email.  */
  async function handleFormSubmit(payload) {
    setError("");
    if (!isEmailLike(payload.email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      setForm(payload || {});
      await finalizeRegistrationAndSend(payload);
    } catch (e) {
      setError("Failed to submit.  Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function finalizeRegistrationAndSend(submittedForm) {
    if (processing) return;
    setProcessing(true);
    setError("");

    try {
      const formData = submittedForm || form || {};
      const name =
        formData.name ||
        `${formData.firstName || ""} ${formData.lastName || ""}`.trim() ||
        "Speaker";

      const payload = {
        ...formData,
        name,
        termsAccepted: !!formData.termsAccepted,
        _rawForm: formData,
      };

      // Save to backend (backend automatically sends ACK email in background)
      const res = await fetch(apiUrl("/api/speakers"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify(payload),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok || !(js && (js.success || js.insertedId || js.id))) {
        const em =
          (js && (js.error || js.message)) || `Save failed (${res.status})`;
        setError(em);
        setProcessing(false);
        return;
      }
      const insertedId = js.insertedId || js.insertId || js.id || null;
      if (insertedId) setSpeakerId(insertedId);

      const savedSpeaker = { ...payload, id: insertedId };
      setSpeaker(savedSpeaker);

      // ✅ Backend handles reminders automatically via scheduleDynamicReminder()
      if (insertedId) {
        console.log(
          "[Speakers] Registration successful, reminder scheduled by backend",
        );
      }

      // Mark submission complete and clear form
      setSubmissionComplete(true);
      setForm({});
    } catch (err) {
      console.error("finalizeRegistrationAndSend error", err);
      setError("Failed to finalize registration.");
    } finally {
      setProcessing(false);
    }
  }

  /* ---------- MOBILE RENDER ---------- */
  if (isMobile) {
    return (
      <div className="min-h-screen w-full bg-white flex items-start justify-center p-4">
        <div className="w-full max-w-md">
          <Topbar />

          {!loading && !submissionComplete && Array.isArray(config?.fields) ? (
            <>
              <div className="mt-4">
                <h2 className="text-xl font-bold text-[#21809b] mb-4 text-center">
                  Speaker Registration
                </h2>
                <DynamicRegistrationForm
                  config={{ ...config, fields: config.fields || [] }}
                  form={form}
                  setForm={setForm}
                  onSubmit={handleFormSubmit}
                  editable={true}
                  registrationType="speaker"
                  submitting={submitting || processing}
                  terms={
                    config && (config.termsUrl || config.termsText)
                      ? {
                          url: config.termsUrl,
                          text: config.termsText,
                          label: config.termsLabel || "Terms & Conditions",
                          required: !!config.termsRequired,
                        }
                      : null
                  }
                />
              </div>
              <div className="mt-3 mb-4" aria-hidden />
            </>
          ) : loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : null}

          {submissionComplete && (
            <div className="mt-4">
              <ThankYouMessage
                email={speaker?.email || form.email}
                messageOverride="Thank you for registering as a speaker. We have received your details and our team will contact you shortly."
              />
            </div>
          )}

          {error && (
            <div className="text-red-600 mt-3 text-center text-sm">{error}</div>
          )}
        </div>
      </div>
    );
  }

  /* ---------- DESKTOP RENDER ---------- */
  return (
    <div className="min-h-screen w-full relative">
      {!isMobile &&
        config?.backgroundMedia?.type === "video" &&
        config?.backgroundMedia?.url && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        )}
      {!config?.backgroundColor &&
        config?.backgroundMedia?.type === "image" &&
        config?.backgroundMedia?.url && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: -999,
              backgroundImage: `url(${config.backgroundMedia.url})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        )}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(255,255,255,0.55)",
          zIndex: -900,
        }}
      />

      <div className="relative z-10">
        <Topbar />
        <div className="max-w-7xl mx-auto pt-8 px-4">
          {/* ✅ Centered Event Details - ADD THIS BLOCK */}
          <div className="flex justify-center items-center mb-8 py-4">
            {loading ? (
              <span className="text-[#21809b] text-xl font-semibold">
                Loading event details...
              </span>
            ) : (
              <EventDetailsBlock
                event={canonicalEvent || config?.eventDetails || null}
              />
            )}
          </div>
          <div className="mb-6">
            <div className="flex items-center">
              <div className="flex-grow border-t border-[#21809b]" />
              <span className="mx-5 px-8 py-3 text-2xl font-extrabold text-[#21809b] bg-white rounded-2xl">
                Register as Speaker
              </span>
              <div className="flex-grow border-t border-[#21809b]" />
            </div>
          </div>

          {/* Registration Form */}
          {!submissionComplete && !loading && Array.isArray(config?.fields) && (
            <div className="max-w-3xl mx-auto">
              <DynamicRegistrationForm
                config={{ ...config, fields: config.fields || [] }}
                form={form}
                setForm={setForm}
                registrationType="speaker"
                onSubmit={handleFormSubmit}
                editable={true}
                submitting={submitting || processing}
                terms={
                  config && (config.termsUrl || config.termsText)
                    ? {
                        url: config.termsUrl,
                        text: config.termsText,
                        label: config.termsLabel || "Terms & Conditions",
                        required: !!config.termsRequired,
                      }
                    : null
                }
              />
            </div>
          )}

          {/* Thank you message component shown after submission */}
          {submissionComplete && (
            <div className="max-w-3xl mx-auto">
              <ThankYouMessage email={speaker?.email || form.email} />
            </div>
          )}

          {!isMobile &&
            config?.backgroundMedia?.type === "video" &&
            !loading && (
              <div className="mt-4 p-3 text-sm text-gray-600">
                Background video active
              </div>
            )}
          {error && (
            <div className="text-red-400 text-center mt-4">{error}</div>
          )}
        </div>
      </div>
      <div className="mt-16">
        <Footer primaryColor={primaryColor} />
      </div>
    </div>
  );
}
